import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import { integratedHealthcareData } from '../mockPages';
import ContactForm from '../components/ContactForm';

const IntegratedHealthcarePage = () => {
  const { t } = useTranslation();

  const getIconComponent = (iconName) => {
    const iconMap = {
      'brain': Icons.Brain,
      'heart': Icons.Heart,
      'stethoscope': Icons.Stethoscope,
      'activity': Icons.Activity,
      'shield': Icons.Shield,
      'users': Icons.Users,
      'leaf': Icons.Leaf,
      'zap': Icons.Zap,
      'target': Icons.Target,
      'clipboard': Icons.ClipboardList,
      'pill': Icons.Pill,
      'refresh': Icons.RefreshCw,
      'globe': Icons.Globe,
    };
    return iconMap[iconName] || Icons.Circle;
  };

  return (
    <div style={{ background: 'var(--bg-page)', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section
        style={{
          background: '#0A1628',
          padding: '5rem 1.2rem',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
            zIndex: 0,
          }}
        />
        
        <div
          style={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '10%',
            width: '250px',
            height: '250px',
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            {t('integratedHealthcare.title')}
          </h1>
          <p className="heading-2" style={{ color: '#22c55e', marginBottom: '2rem', textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>
            {t('integratedHealthcare.subtitle')}
          </p>
          <p
            className="body-large"
            style={{
              maxWidth: '900px',
              margin: '0 auto',
              color: 'var(--text-secondary)',
              lineHeight: '1.8',
            }}
          >
            {t('integratedHealthcare.description')}
          </p>
        </div>
      </section>

      {/* What is Integrated Healthcare */}
      <section style={{ padding: '5rem 1.2rem', background: 'var(--bg-section)' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '3rem',
              alignItems: 'center',
            }}
          >
            <div>
              <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
                {t('integratedHealthcare.definitionTitle')}
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '1.5rem' }}>
                {t('integratedHealthcare.definitionContent')}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[1, 2, 3, 4, 5].map((num) => (
                  <li
                    key={num}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <Icons.CheckCircle size={20} color="#22c55e" />
                    <span className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                      {t(`integratedHealthcare.keyPoint${num}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              style={{
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              }}
            >
              <img
                src="1000498484.png"
                alt="Integrated Healthcare"
                style={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '2rem',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                }}
              >
                <p className="body-medium" style={{ color: 'white', fontWeight: '500' }}>
                  Blending Traditional Wisdom with Modern Science
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section style={{ padding: '5rem 1.2rem', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              {t('integratedHealthcare.approachTitle')}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t('integratedHealthcare.approachSubtitle')}
            </p>
          </div>

          <div
            className="integrated-approach-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '2rem',
            }}
          >
            {[
              { key: 1, icon: 'brain' },
              { key: 2, icon: 'activity' },
              { key: 3, icon: 'clipboard' },
              { key: 4, icon: 'refresh' },
              { key: 5, icon: 'globe' },
              { key: 6, icon: 'heart' },
            ].map((item, index) => {
              const IconComponent = getIconComponent(item.icon);
              const colors = [
                { bg: 'var(--accent-blue)', icon: 'icon-container-ai' },
                { bg: 'var(--accent-green)', icon: 'icon-container-ayurveda' },
                { bg: 'var(--accent-amber)', icon: 'icon-container-healing' },
                { bg: 'var(--accent-teal)', icon: 'icon-container-ai' },
                { bg: 'var(--accent-purple)', icon: 'icon-container-ayurveda' },
                { bg: 'var(--accent-pink)', icon: 'icon-container-healing' },
              ];
              const color = colors[index % colors.length];

              return (
                <div
                  key={item.key}
                  style={{
                    background: color.bg,
                    borderRadius: '1rem',
                    padding: '2rem',
                    border: '1px solid var(--border-light)',
                    transition: 'all 0.3s ease',
                  }}
                  className="hover-lift"
                >
                  <div
                    className={color.icon}
                    style={{
                      width: '3.5rem',
                      height: '3.5rem',
                      borderRadius: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.25rem',
                    }}
                  >
                    <IconComponent size={24} color="white" />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    {t(`integratedHealthcare.approach${item.key}Title`)}
                  </h3>
                  <p className="body-small" style={{ marginBottom: '1.25rem', color: 'var(--text-secondary)' }}>
                    {t(`integratedHealthcare.approach${item.key}Desc`)}
                  </p>
                  {integratedHealthcareData.approach[index]?.benefits && (
                    <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '1rem' }}>
                      <p className="body-small" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                        {t('integratedHealthcare.keyBenefits')}:
                      </p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {integratedHealthcareData.approach[index]?.benefits?.map((benefit, idx) => (
                          <li
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.5rem',
                            }}
                          >
                            <Icons.Check size={16} color="var(--forest-green-600)" />
                            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              {benefit}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};

export default IntegratedHealthcarePage;
